// ============================================================================
// Google Calendar — Watch (push notification) helpers.
//
// Google Calendar publishes push notifications via the Events Watch API:
//   POST /calendar/v3/calendars/{calendarId}/events/watch
//   Body: {
//     id: <channelId>,         // we mint a ULID
//     type: "web_hook",
//     address: <webhookUrl>,   // must be HTTPS + verified domain
//     token: <channelToken>,   // shared secret — echoed back on every push
//     expiration: <unixMs>     // OPTIONAL; Google caps at 30 days
//   }
//
// Stop channel:
//   POST /calendar/v3/channels/stop
//   Body: { id, resourceId }   // both returned by the subscribe call
//
// When the body is empty (every Calendar push is) we learn only that the
// calendar changed. Downstream pipelines should then call `listGoogleEvents`
// with the stored `syncToken` to compute the delta.
//
// Canonical docs:
//   - https://developers.google.com/calendar/api/guides/push
//   - https://developers.google.com/calendar/api/v3/reference/events/watch
// ============================================================================

import { GOOGLE_CALENDAR_API_BASE } from './oauth'
import {
  GoogleCalendarError,
  classifyGoogleError,
  parseGoogleRetryAfterMs,
  type GoogleErrorEnvelope,
  type GoogleWatchResponse,
} from './types'

// ============================================================================
// Constants
// ============================================================================

/** Google caps Calendar push channels at 30 days (2_592_000_000 ms). */
export const GOOGLE_WATCH_MAX_TTL_MS = 30 * 24 * 60 * 60 * 1000
/** Bytes of entropy in a channel token — enough to resist offline brute force. */
const CHANNEL_TOKEN_BYTES = 32

// ============================================================================
// subscribeCalendarChanges
// ============================================================================

export interface SubscribeCalendarChangesInput {
  accessToken: string
  calendarId: string
  webhookUrl: string
  /** Caller-supplied channel id — MUST be unique per subscription. When omitted
   *  we mint a ULID-like 26-char id. */
  channelId?: string
  /** Caller-supplied shared-secret token. When omitted we mint 32 random bytes
   *  of hex (64 chars). Whatever we end up using is returned so the caller
   *  can persist it + later use it to verify inbound webhooks. */
  channelToken?: string
  /** ISO 8601 absolute expiration. Google caps at now + 30 days; we clamp. */
  expiration?: string
  /** Override the API base URL (tests). */
  apiBase?: string
}

export interface SubscribeCalendarChangesOutput {
  channelId: string
  /** Opaque provider id, returned by Google. Required for unsubscription. */
  resourceId: string
  /** ISO 8601 absolute expiration returned by Google. */
  expiration: string
  /** The shared secret we sent (or minted) — caller persists on the instance config. */
  channelToken: string
}

/**
 * Subscribe to changes on `calendarId`. Returns the channel id + resource id
 * Google assigned, plus the shared-secret token we sent so the caller can
 * verify incoming webhook notifications.
 */
export async function subscribeCalendarChanges(
  input: SubscribeCalendarChangesInput,
  fetchImpl: typeof fetch = fetch,
): Promise<SubscribeCalendarChangesOutput> {
  assertAccessToken(input.accessToken)
  assertCalendarId(input.calendarId)
  assertWebhookUrl(input.webhookUrl)

  const channelId = input.channelId ?? mintChannelId()
  const channelToken = input.channelToken ?? mintChannelToken()
  const expirationMs = resolveExpirationMs(input.expiration)

  const base = input.apiBase ?? GOOGLE_CALENDAR_API_BASE
  const url = `${base}/calendars/${encodeURIComponent(input.calendarId)}/events/watch`

  const body: {
    id: string
    type: string
    address: string
    token: string
    expiration?: string
  } = {
    id: channelId,
    type: 'web_hook',
    address: input.webhookUrl,
    token: channelToken,
  }
  // Google wants the expiration as a string of milliseconds since epoch.
  if (expirationMs !== undefined) body.expiration = String(expirationMs)

  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${input.accessToken}`,
    },
    body: JSON.stringify(body),
  })

  if (!resp.ok) {
    throw await errorFromResponse(resp, 'watch')
  }

  const raw = (await resp.json().catch(() => null)) as GoogleWatchResponse | null
  if (!raw || typeof raw.resourceId !== 'string' || raw.resourceId.length === 0) {
    throw new GoogleCalendarError(
      'PROVIDER_ERROR',
      'Google watch response missing resourceId',
    )
  }

  // Google echoes back the channel id + an expiration (always) and
  // optionally a nudged resource URI. We return the channel token we sent —
  // it does NOT come back in the response, by design (it's a secret).
  //
  // Issue #254 L1: Google's docs say `expiration` is always returned, but
  // we've seen sporadic responses where the field is missing. When we fall
  // back to synthesising `now + 30d`, warn to Logpush so ops can correlate
  // early-expiration incidents with Google responses that never carried the
  // expected field.
  let expiration: string
  if (typeof raw.expiration === 'string') {
    expiration = msToIso(Number(raw.expiration))
  } else {
    console.warn(
      '[google-calendar/watch] Google response missing expiration; synthesising now + 30d',
    )
    expiration = new Date(
      Date.now() + (expirationMs ?? GOOGLE_WATCH_MAX_TTL_MS),
    ).toISOString()
  }

  return {
    channelId: typeof raw.id === 'string' && raw.id.length > 0 ? raw.id : channelId,
    resourceId: raw.resourceId,
    expiration,
    channelToken,
  }
}

// ============================================================================
// unsubscribeCalendarChannel
// ============================================================================

export interface UnsubscribeCalendarChannelInput {
  accessToken: string
  channelId: string
  resourceId: string
  apiBase?: string
}

/**
 * Stop a Calendar watch channel. Google returns 204 on success, 404 on an
 * already-stopped / unknown channel. We treat 404 as success (idempotent
 * semantics) so cleanup code doesn't have to special-case "already gone".
 */
export async function unsubscribeCalendarChannel(
  input: UnsubscribeCalendarChannelInput,
  fetchImpl: typeof fetch = fetch,
): Promise<void> {
  assertAccessToken(input.accessToken)
  if (!input.channelId || typeof input.channelId !== 'string') {
    throw new GoogleCalendarError('INVALID_INPUT', 'channelId is required')
  }
  if (!input.resourceId || typeof input.resourceId !== 'string') {
    throw new GoogleCalendarError('INVALID_INPUT', 'resourceId is required')
  }

  const base = input.apiBase ?? GOOGLE_CALENDAR_API_BASE
  const url = `${base}/channels/stop`

  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${input.accessToken}`,
    },
    body: JSON.stringify({ id: input.channelId, resourceId: input.resourceId }),
  })

  if (resp.ok || resp.status === 204 || resp.status === 404) return
  throw await errorFromResponse(resp, 'watch')
}

// ============================================================================
// Token generation
// ============================================================================

/**
 * Generate a 32-byte random channel token in hex form. Used as a shared
 * secret: we send it in the subscribe call, Google echoes it back on every
 * push notification, and our webhook verifier timing-safe-compares it
 * against the stored value.
 */
export function mintChannelToken(): string {
  const bytes = new Uint8Array(CHANNEL_TOKEN_BYTES)
  crypto.getRandomValues(bytes)
  let out = ''
  for (const b of bytes) out += b.toString(16).padStart(2, '0')
  return out
}

/**
 * Generate a ULID-like identifier (Crockford base32, 26 chars). We don't
 * pull in a proper ULID dep — the id only needs to be URL-safe and
 * collision-resistant, and we have plenty of entropy (80 bits random +
 * 48 bits of ms-timestamp).
 */
export function mintChannelId(): string {
  const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ' // Crockford base32
  const now = Date.now()
  const tsBuf = new Uint8Array(6)
  tsBuf[0] = (now / 2 ** 40) & 0xff
  tsBuf[1] = (now / 2 ** 32) & 0xff
  tsBuf[2] = (now / 2 ** 24) & 0xff
  tsBuf[3] = (now / 2 ** 16) & 0xff
  tsBuf[4] = (now / 2 ** 8) & 0xff
  tsBuf[5] = now & 0xff
  const rand = new Uint8Array(10)
  crypto.getRandomValues(rand)
  const buf = new Uint8Array(16)
  buf.set(tsBuf, 0)
  buf.set(rand, 6)
  // Encode 128 bits as 26 Crockford base32 chars. We use a simple bit-walking
  // encode rather than a lookup table to keep the code short + allocation-free.
  let out = ''
  let bits = 0
  let value = 0
  for (let i = 0; i < buf.length; i++) {
    value = (value << 8) | buf[i]
    bits += 8
    while (bits >= 5) {
      bits -= 5
      out += ALPHABET[(value >>> bits) & 0x1f]
    }
  }
  if (bits > 0) out += ALPHABET[(value << (5 - bits)) & 0x1f]
  return out.slice(0, 26)
}

// ============================================================================
// Internals
// ============================================================================

function resolveExpirationMs(isoExpiration: string | undefined): number | undefined {
  if (!isoExpiration) return undefined
  const ms = Date.parse(isoExpiration)
  if (!Number.isFinite(ms) || ms <= Date.now()) {
    throw new GoogleCalendarError(
      'INVALID_INPUT',
      'expiration must be an ISO 8601 timestamp in the future',
    )
  }
  // Clamp to Google's 30-day ceiling. Round 1 H4: warn when clamping so
  // ops can spot callers that are asking for longer TTLs than Google will
  // honour (e.g. a misconfigured scheduler that computed an expiry > 30d
  // out). We log to console here instead of plumbing a `logger` through
  // every call site — `console.warn` is the tenant-worker convention and
  // gets picked up by Logpush.
  const maxMs = Date.now() + GOOGLE_WATCH_MAX_TTL_MS
  if (ms > maxMs) {
    console.warn(
      `[google-calendar/watch] clamped expiration ${isoExpiration} to Google's 30-day max`,
    )
    return maxMs
  }
  return ms
}

function msToIso(ms: number): string {
  if (!Number.isFinite(ms)) return new Date().toISOString()
  return new Date(ms).toISOString()
}

async function errorFromResponse(
  resp: Response,
  resourceKind: 'calendar' | 'event' | 'watch' | 'other',
): Promise<GoogleCalendarError> {
  let envelope: GoogleErrorEnvelope | null = null
  try {
    envelope = (await resp.json()) as GoogleErrorEnvelope
  } catch {
    // Non-JSON error body — leave envelope null.
  }
  const retryAfterMs = parseGoogleRetryAfterMs(resp.headers.get('Retry-After'))
  return classifyGoogleError(resp.status, envelope, resourceKind, retryAfterMs)
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new GoogleCalendarError('INVALID_INPUT', 'accessToken is required')
  }
}

function assertCalendarId(calendarId: string | undefined | null): void {
  if (!calendarId || typeof calendarId !== 'string' || calendarId.trim().length === 0) {
    throw new GoogleCalendarError('INVALID_INPUT', 'calendarId is required')
  }
}

function assertWebhookUrl(url: string | undefined | null): void {
  if (!url || typeof url !== 'string') {
    throw new GoogleCalendarError('INVALID_INPUT', 'webhookUrl is required')
  }
  if (!url.startsWith('https://')) {
    // Google rejects non-HTTPS webhook URLs at the API level too, but failing
    // fast on our side gives a cleaner error path.
    throw new GoogleCalendarError(
      'INVALID_INPUT',
      'webhookUrl must be HTTPS (Google Calendar push requires HTTPS + verified domain)',
    )
  }
}
